import axios, { type CancelTokenSource } from 'axios';
import {
    type AddRefundPreApplyStep,
    BillErrors,
    type BillListResponse,
    type BillResponse,
    BillsApiApiBillsBillCodeFullRefundGet,
    type BillsApiApiBillsBillCodeFullRefundGetRequest,
    BillsApiApiBillsBillCodeGet,
    type BillsApiApiBillsBillCodeGetRequest,
    BillsApiApiBillsGet,
    type BillsApiApiBillsGetRequest,
    type FullRefundResponse,
    type PenaltyInquiryResponse,
    type RefundHandlerResponse,
    type ReserveResponse,
    SuperPNRsApiApiSuperPNRsSuperPNRIdDelete,
    SuperPNRsApiApiSuperPNRsSuperPNRIdPenaltyPost,
    SuperPNRsApiApiSuperPNRsSuperPnrIdPreApplyRefundPost,
    type TicketPenalty,
} from '@clients/BillApi';
import { LoadingStatus } from '@itinerary/ItineraryModuleTypes';

export default class TrackingStore {
    //#region state
    /**
     * List of loaded bills related to the current user
     *
     * @type {BillResponse[]}
     * @memberof TrackingStore
     */
    bills: BillResponse[] = [];
    prevBills: BillResponse[] = [];
    ongoingBills: BillResponse[] = [];
    ongoingBillRequestSource: CancelTokenSource | null = null;
    refunds: FullRefundResponse[] = [];
    refundIsCancellationExternal = false;
    refundAlert = '';
    refundUrl = '';
    /**
     * Count of pages of bills to keep track of
     *
     * @type {number | null}
     * @memberof TrackingStore
     */
    pageCount: number | null = null;

    /**
     * Total count of bills, regardless of pagination
     *
     * @type {number | null}
     * @memberof TrackingStore
     */
    totalCount: number | null = null;

    /**
     * Indicates the current page number
     *
     * @type {number}
     * @memberof TrackingStore
     */
    currentPage = 0;

    /**
     * Indicates the loading status of the current page
     *
     * @type {LoadingStatus}
     * @memberof TrackingStore
     */
    loadingStatus: LoadingStatus = LoadingStatus.None;

    /**
     * Indicates whether all the bill of user are loaded, or not
     *
     * @type {boolean}
     * @memberof TrackingStore
     */
    loadingFinished = false;

    /**
     * Keep current super pnr id for details of current reserve and current bill
     *
     * @type {(number | null)}
     * @memberof TrackingStore
     */
    currentSuperPNRId: number | null = null;

    /**
     * Loading status of penalties of trip cancellation
     *
     * @type {LoadingStatus}
     * @memberof TrackingStore
     */
    penaltyLoadingStatus: LoadingStatus = LoadingStatus.None;

    /**
     * List of penalties of cancellation of trips
     *
     * @type {TicketPenalty[] | null}
     * @memberof TrackingStore
     */
    penalties: TicketPenalty[] | null = null;

    /**
     * List of methods of refund that can be chosen in the refund process
     *
     * @type {RefundHandlerResponse[] | null}
     * @memberof TrackingStore
     */
    refundHandlers: RefundHandlerResponse[] | null = null;

    /**
     * List of penalties that is selected by the user to be applied in the refund process
     *
     * @type {TicketPenalty[] | null}
     * @memberof TrackingStore
     */
    selectedPenalties: TicketPenalty[] | null = null;

    /**
     * The selected refund method, by the user, from the list of refund handlers
     *
     * @type {number | null}
     * @memberof TrackingStore
     */
    selectedMethodId: number | null = null;

    /**
     * Number of the debit card, that the money is going to be refunded to
     *
     * @type {string | null}
     * @memberof TrackingStore
     */
    cardNo: string | null = null;

    /**
     * Name of the holder of the debit card, that the money is going to be refunded to
     *
     * @type {string | null}
     * @memberof TrackingStore
     */
    cardHolderName: string | null = null;

    /**
     * Cancellation status of the trip
     *
     * @type {LoadingStatus}
     * @memberof TrackingStore
     */
    cancellationStatus: LoadingStatus = LoadingStatus.None;

    /**
     * Specifies the refund expiration time
     *
     * @type {string | null}
     * @memberof TrackingStore
     */
    refundExpirationTime: string | null = null;
    totalPenalty: number | null = null;
    creditCardInfo: AddRefundPreApplyStep | null = null;
    //#endregion

    /**
     * Loads the result of currently reserved trips bill
     *
     * @param {BillListResponse} result
     * @param currentState
     * @memberof TrackingStore
     */
    loadBillsResult(result: BillListResponse, currentState?: 0 | 1) {
        this.bills = this.bills.concat(result.Bills || []);
        if (currentState === 0) {
            this.ongoingBills = this.ongoingBills.concat(result.Bills || []);
        } else if (currentState === 1) {
            this.prevBills = this.prevBills.concat(result.Bills || []);
        }
        const totalCount = result.Meta?.TotalCount || 0;
        this.pageCount = Math.ceil(totalCount / (result.Meta?.PageSize || 0));
        this.totalCount = totalCount;
        this.loadingFinished = this.bills.length >= totalCount;
    }

    /**
     * Loads a single selected bill
     *
     * @param {BillResponse} bill
     * @memberof TrackingStore
     */
    loadSingleBill(bill: BillResponse) {
        if (!bill) {
            return;
        }
        this.bills = [bill];
    }

    /**
     * Clears all bills of reserved trips
     *
     * @memberof TrackingStore
     */
    clearBills() {
        this.bills = [];
    }

    /**
     * Resets all the reserved bills
     *
     * @memberof TrackingStore
     */
    reset() {
        this.currentPage = 0;
        this.bills = [];
        this.ongoingBills = [];
        this.prevBills = [];
    }

    /**
     * reset state of tracking module
     *
     * @memberof AuthStore
     */
    resetState() {
        this.bills = [];
        this.pageCount = null;
        this.totalCount = null;
        this.currentPage = 0;
        this.loadingStatus = LoadingStatus.None;
        this.loadingFinished = false;
        this.currentSuperPNRId = null;
        this.penaltyLoadingStatus = LoadingStatus.None;
        this.penalties = null;
        this.refundHandlers = null;
        this.selectedPenalties = null;
        this.selectedMethodId = null;
        this.cardNo = null;
        this.cardHolderName = null;
        this.cancellationStatus = LoadingStatus.None;
        this.refundExpirationTime = null;
        this.creditCardInfo = null;
    }

    /**
     * Initiates the tracking information
     *
     * @memberof TrackingStore
     */
    init() {
        this.penalties = null;
        this.refundHandlers = null;
        this.selectedPenalties = null;
        this.selectedMethodId = null;
    }

    /**
     * Sets penalty results of reserved bills
     *
     * @param {PenaltyInquiryResponse} result
     * @memberof TrackingStore
     */
    setPenaltyResult(result: PenaltyInquiryResponse) {
        this.totalPenalty = result.TotalPenalty || null;
        this.refundExpirationTime = result.ExpirationTime || null;
        this.penalties = result.Penalties || null;
        this.refundHandlers = result.RefundHandlers || null;
        this.refundIsCancellationExternal = !!result.IsCancellationExternal;
        this.refundAlert = result.ExternalCancellationAlert || '';
        this.refundUrl = result.ExternalCancellationUrl || '';
    }

    /**
     * GETTER - return the current reserved trip
     *
     * @memberof TrackingStore
     */
    get currentReserve(): ReserveResponse | null {
        return (
            this.currentBill?.Reserves?.find(
                r => r.SuperPNRId === this.currentSuperPNRId
            ) || null
        );
    }

    /**
     * GETTER - return the bill associated with the current reserved trip
     *
     * @memberof TrackingStore
     */
    get currentBill(): BillResponse | null {
        return (
            this.bills.find(bill =>
                bill.Reserves?.find(
                    reserve => reserve.SuperPNRId === this.currentSuperPNRId
                )
            ) || null
        );
    }

    get disableLoadMoreBills(): boolean {
        return (
            this.loadingStatus !== LoadingStatus.Success || this.loadingFinished
        );
    }

    /**
     * ACTION - Loads user's bills from server
     *
     * @async
     * @param {{
     *      reset: boolean;
     *      billCode?: string;
     *      passengerName?: string;
     *      from?: string;
     *      to?: string;
     *      SearchModes?: string[];
     *  }} payload
     * @param currentState
     * @memberof TrackingStore
     */
    async loadUserBills(
        payload: BillsApiApiBillsGetRequest = {},
        currentState?: 0 | 1
    ) {
        this.currentPage++;
        this.loadingStatus = LoadingStatus.Loading;
        try {
            if (this.ongoingBillRequestSource) {
                this.ongoingBillRequestSource.cancel('due to tab change');
            }
            const source = axios.CancelToken.source();
            this.ongoingBillRequestSource = source;
            const { data } = await BillsApiApiBillsGet(
                {
                    ...payload,
                    pageNumber: this.currentPage,
                },
                {
                    cancelToken: source.token,
                }
            );
            this.ongoingBillRequestSource = null;
            this.loadBillsResult(data, currentState);
            this.loadingStatus = LoadingStatus.Success;
        } catch (e) {
            reportException(e);
            this.loadingStatus = LoadingStatus.Error;
        }
    }

    /**
     * ACTION - Loads the selected bill from the server
     *
     * @async
     * @param {{ billCode: string; mobile: string }} payload
     * @memberof TrackingStore
     */
    async loadBill(payload: BillsApiApiBillsBillCodeGetRequest) {
        this.loadingStatus = LoadingStatus.Loading;
        this.clearBills();
        try {
            const { data, status } = await BillsApiApiBillsBillCodeGet(payload);
            if (status === 204) {
                throw BillErrors.NotFound;
            }
            this.loadSingleBill(data);
            this.loadingStatus = LoadingStatus.Success;
        } catch (error) {
            this.loadingStatus =
                error === BillErrors.NotFound
                    ? LoadingStatus.Success
                    : LoadingStatus.Error;
            throw error;
        }
    }

    async getRefunds(params: BillsApiApiBillsBillCodeFullRefundGetRequest) {
        const { data } = await BillsApiApiBillsBillCodeFullRefundGet(params);
        this.refunds = data;
    }

    /**
     * ACTION - Loads the penalty of current reservation
     *
     * @async
     * @memberof TrackingStore
     */
    async loadPenalty(currentReserve: ReserveResponse | undefined) {
        if (currentReserve === undefined) {
            return;
        }

        this.penaltyLoadingStatus = LoadingStatus.Loading;
        try {
            const { data } =
                await SuperPNRsApiApiSuperPNRsSuperPNRIdPenaltyPost({
                    superPNRId: currentReserve.SuperPNRId || 0,
                    superPNRPenaltyRequest: {
                        TicketIds: currentReserve.Tickets?.map(t => t.Id || 0),
                    },
                });
            this.setPenaltyResult(data);
            this.penaltyLoadingStatus = LoadingStatus.Success;
        } catch (ex) {
            this.penaltyLoadingStatus = LoadingStatus.Error;
            throw ex;
        }
    }

    /**
     * ACTION - Performs the cancellation with the selected penalty and method on the current reservation
     *
     * @async
     * @returns {Promise<string>}
     * @memberof TrackingStore
     */
    async performCancellation(
        currentReserve: ReserveResponse | undefined
    ): Promise<string> {
        if (
            this.selectedPenalties == null ||
            this.selectedMethodId == null ||
            currentReserve === undefined
        ) {
            throw new Error('Invalid state');
        }
        this.cancellationStatus = LoadingStatus.Loading;
        try {
            const { data: deleteData } =
                await SuperPNRsApiApiSuperPNRsSuperPNRIdDelete({
                    superPNRId: currentReserve.SuperPNRId || 0,
                    cancelAfterReserveRequest: {
                        RefundHandlerId: this.selectedMethodId,
                        CardNumber: this.cardNo,
                        CardHolder: this.cardHolderName,
                        PreApplyRefundTicket:
                            this.creditCardInfo?.PreApplyTicket,
                        Tickets: this.selectedPenalties.map(p => ({
                            Id: p.Ticket?.Id,
                            Penalty:
                                (p.Penalty || p.PenaltyPercent) ?? undefined,
                            PenaltyType: p.PenaltyType,
                        })),
                    },
                });

            const currentBill =
                this.bills.find(bill =>
                    bill.Reserves?.find(
                        reserve =>
                            reserve.SuperPNRId === currentReserve.SuperPNRId
                    )
                ) || null;

            const billCode = currentBill?.BillCode || '';
            const mobile = currentBill?.Mobile || '';
            const { data } = await BillsApiApiBillsBillCodeGet({
                billCode,
                mobile,
            });
            const index = this.bills.findIndex(
                bill => bill.BillCode === billCode && bill.Mobile === mobile
            );
            this.cancellationStatus = LoadingStatus.Success;
            if (index === -1) {
                return deleteData.Message || '';
            }
            this.bills[index] = data;

            return deleteData.Message || '';
        } catch (ex) {
            this.cancellationStatus = LoadingStatus.Error;
            throw ex;
        }
    }

    async getCardDetails(currentReserve: ReserveResponse | undefined) {
        if (
            this.selectedPenalties == null ||
            this.selectedMethodId == null ||
            this.cardNo === null ||
            currentReserve === undefined
        ) {
            throw new Error('Invalid state');
        }
        this.cancellationStatus = LoadingStatus.Loading;
        try {
            const { data } =
                await SuperPNRsApiApiSuperPNRsSuperPnrIdPreApplyRefundPost({
                    superPnrId: currentReserve.SuperPNRId || 0,
                    preApplyRefundRequest: {
                        RefundHandlerId: this.selectedMethodId,
                        CardNumber: this.cardNo,
                        Amount: 0,
                    },
                });
            this.cancellationStatus = LoadingStatus.Success;
            this.creditCardInfo = data;
        } catch (e) {
            this.cancellationStatus = LoadingStatus.Error;
            throw e;
        }
    }
}

export const { use: useTrackingStore } = defineClassStore(
    'tracking',
    () => new TrackingStore()
);
